import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import PageHeadingSection from '../../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../../services/apiService';
import CustomSelect from '../../../../../common/formfields/CustomSelect';
import Breadcrumbs from '../../../../../common/BreadCrum/BreadCrum';
import { primaryColor, secondaryColor } from '../../../../../../utils/comon';

const ParkingTypeMasterForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const { user } = useSelector((state) => state.auth);
    const siteId = user?.siteId;

    const [formData, setFormData] = useState({
        typeName: '',
        structureType: '',
        capacity: 0,
        isActive: true
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;
    const rowData = location.state?.rowData;

    useEffect(() => {
        if (isEdit && rowData) {
            setFormData({
                typeName: rowData.typeName || rowData.TypeName || '',
                structureType: rowData.structureType || rowData.StructureType || '',
                capacity: rowData.capacity || rowData.Capacity || 0,
                isActive: rowData.isActive !== undefined ? rowData.isActive : (rowData.IsActive !== undefined ? rowData.IsActive : true)
            });
        }
    }, [isEdit, rowData]);

    const handleInputChange = (e) => {
        const { id, value, type } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [id]: type === 'number' ? parseInt(value) || 0 : value 
        }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : '' }));
    };

    const validateForm = () => {
        if (!formData.typeName || !formData.typeName.trim()) return toast.warning("Type Name is required"), false;
        if (!formData.structureType || !formData.structureType.trim()) return toast.warning("Structure Type is required"), false;
        if (formData.capacity < 0) return toast.warning("Capacity cannot be negative"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            id: isEdit ? editId : 0,
            siteId: siteId || 0,
            typeName: formData.typeName,
            structureType: formData.structureType,
            capacity: formData.capacity,
            isActive: formData.isActive,
            createdBy: user?.name || "System"
        };

        try {
            const url = isEdit ? `/ParkingTypeMaster/${editId}` : `/ParkingTypeMaster`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response?.data?.isSuccess === true || response?.data?.IsSuccess === true) {
                toast.success(`Parking type ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentcomm/parking-type-master');
            } else {
                toast.error(response?.data?.message || response?.data?.Message || `Failed to ${isEdit ? 'update' : 'create'} parking type.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading={isEdit ? "Edit Parking Type" : "Add Parking Type"}
                subHeading={isEdit ? "Update parking type details" : "Create a new parking type"}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                {/* Type Name */}
                <div>
                    <label htmlFor="typeName" className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 uppercase">
                        TYPE NAME <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="typeName"
                        value={formData.typeName}
                        onChange={handleInputChange}
                        placeholder="e.g. Visitor Parking"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                    />
                </div>

                {/* Structure Type */}
                <div>
                    <label htmlFor="structureType" className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 uppercase">
                        STRUCTURE TYPE <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="structureType"
                        value={formData.structureType}
                        onChange={handleInputChange}
                        placeholder="e.g. Standard"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                    />
                </div>

                {/* Capacity */}
                <div>
                    <label htmlFor="capacity" className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1 uppercase">
                        CAPACITY <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        id="capacity"
                        value={formData.capacity}
                        onChange={handleInputChange}
                        min="0"
                        placeholder="e.g. 50"
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                    />
                </div>

                {/* Status */}
                <CustomSelect
                    label={<>STATUS <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: true }, { label: 'Inactive', value: false }]}
                    value={formData.isActive ? { label: 'Active', value: true } : { label: 'Inactive', value: false }}
                    onChange={(opt) => setFormData(prev => ({ ...prev, isActive: opt ? opt.value : true }))}
                    placeholder="Select"
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate('/zentcomm/parking-type-master')}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default ParkingTypeMasterForm;
